import * as Dialog from '@radix-ui/react-dialog';
import { Check, Loader2, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';

interface TemplateMultiParte {
  id: string;
  nome: string;
  descricao?: string;
  tipoMaquina?: string;
  grupo?: {
    id: string;
    nome: string;
  };
  totalPartes: number;
  totalRiscos: number;
  ativo: boolean;
  createdAt: string;
}

interface TemplateSimples {
  id: string;
  nome: string;
  tipo: string;
  descricao?: string;
  norma?: string;
  ativo: boolean;
  createdAt: string;
  riscosPadrao?: any[];
}

interface TemplateGrupo {
  id: string;
  nome: string;
}

interface ParteTemplate {
  nome: string;
}

type OrigemTemplate = 'multiparte' | 'simples';

interface TemplateLista {
  id: string;
  origem: OrigemTemplate;
  nome: string;
  descricao?: string;
  createdAt: string;
  grupoNome?: string;
  tipoMaquina?: string;
  norma?: string;
  totalPartes: number;
  totalRiscos: number;
}

interface ModalSelecionarTemplateMultiParteProps {
  aberto: boolean;
  onFechar: () => void;
  apreciacaoId: string;
  projetoId?: string;
  onSucesso?: () => void;
}

export function ModalSelecionarTemplateMultiParte({
  aberto,
  onFechar,
  apreciacaoId,
  projetoId,
  onSucesso,
}: ModalSelecionarTemplateMultiParteProps) {
  const { t, formatarData } = useI18n();
  const [templatesMultiparte, setTemplatesMultiparte] = useState<TemplateMultiParte[]>([]);
  const [templatesSimples, setTemplatesSimples] = useState<TemplateSimples[]>([]);
  const [grupos, setGrupos] = useState<TemplateGrupo[]>([]);
  const [carregando, setCarregando] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroGrupo, setFiltroGrupo] = useState<string>('');
  const [filtroTipoMaquina, setFiltroTipoMaquina] = useState<string>('');
  const [templateSelecionado, setTemplateSelecionado] = useState<TemplateLista | null>(null);
  const [partesTemplate, setPartesTemplate] = useState<ParteTemplate[]>([]);
  const [partesSelecionadas, setPartesSelecionadas] = useState<string[]>([]);
  const [carregandoPartes, setCarregandoPartes] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (aberto && projetoId) {
      void carregarGrupos();
      setTemplateSelecionado(null);
      setPartesTemplate([]);
      setPartesSelecionadas([]);
      setErro(null);
    }
  }, [aberto, projetoId]);

  useEffect(() => {
    if (aberto && projetoId) {
      const timeoutId = setTimeout(() => {
        void carregarTemplates();
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [aberto, projetoId, busca, filtroGrupo, filtroTipoMaquina]);

  const carregarGrupos = async () => {
    if (!projetoId) return;

    try {
      const response = await apiService.listarGrupos({
        projetoId,
        ativo: true,
        limit: 100
      });

      if (response.success) {
        setGrupos(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error);
    }
  };

  const carregarTemplates = async () => {
    if (!projetoId) return;

    setCarregando(true);
    setErro(null);

    try {
      const [respMultiparte, respSimples] = await Promise.all([
        apiService.listarTemplateMultiPartes({
          projetoId,
          grupoId: filtroGrupo || undefined,
          tipoMaquina: filtroTipoMaquina || undefined,
          busca: busca || undefined,
          ativo: true,
          limit: 30
        }),
        filtroGrupo
          ? Promise.resolve({ success: true, data: [], message: '' })
          : apiService.listarTemplates({
            projetoId,
            tipo: filtroTipoMaquina || undefined,
            busca: busca || undefined,
            ativo: true,
            limit: 100
          })
      ]);

      if (!respMultiparte.success) {
        setErro(respMultiparte.message || t('apreciacaoModais.selecionarTemplateMultiparte.erroCarregarMultiparte'));
      }

      if (!respSimples.success) {
        setErro(respSimples.message || t('apreciacaoModais.selecionarTemplateMultiparte.erroCarregarSimples'));
      }

      setTemplatesMultiparte(respMultiparte.success && Array.isArray(respMultiparte.data) ? respMultiparte.data : []);
      setTemplatesSimples(respSimples.success && Array.isArray(respSimples.data) ? respSimples.data : []);
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      setErro(t('apreciacaoModais.selecionarTemplateMultiparte.erroCarregar'));
      setTemplatesMultiparte([]);
      setTemplatesSimples([]);
    } finally {
      setCarregando(false);
    }
  };

  const templatesCombinados = useMemo<TemplateLista[]>(() => {
    const itensMultiparte: TemplateLista[] = templatesMultiparte.map((template) => ({
      id: template.id,
      origem: 'multiparte',
      nome: template.nome,
      descricao: template.descricao,
      createdAt: template.createdAt,
      grupoNome: template.grupo?.nome,
      tipoMaquina: template.tipoMaquina,
      totalPartes: template.totalPartes,
      totalRiscos: template.totalRiscos
    }));

    const itensSimples: TemplateLista[] = templatesSimples.map((template) => {
      const riscosPadrao = Array.isArray(template.riscosPadrao) ? template.riscosPadrao : [];
      const partes = new Set<string>();

      for (const risco of riscosPadrao) {
        const parteNome = typeof risco?.parte === 'string' && risco.parte.trim() !== ''
          ? risco.parte.trim()
          : 'Sem parte definida';
        partes.add(parteNome);
      }

      return {
        id: template.id,
        origem: 'simples',
        nome: template.nome,
        descricao: template.descricao,
        createdAt: template.createdAt,
        tipoMaquina: template.tipo,
        norma: template.norma,
        totalPartes: partes.size,
        totalRiscos: riscosPadrao.length
      };
    });

    return [...itensMultiparte, ...itensSimples].sort((a, b) => (
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ));
  }, [templatesMultiparte, templatesSimples]);

  const tiposMaquinaUnicos = useMemo(() => {
    const tiposMulti = templatesMultiparte
      .map((template) => template.tipoMaquina)
      .filter((tipo): tipo is string => Boolean(tipo));

    const tiposSimples = templatesSimples
      .map((template) => template.tipo)
      .filter((tipo): tipo is string => Boolean(tipo));

    return Array.from(new Set([...tiposMulti, ...tiposSimples])).sort();
  }, [templatesMultiparte, templatesSimples]);

  const carregarPartesTemplate = async (template: TemplateLista) => {
    setCarregandoPartes(true);
    try {
      if (template.origem === 'multiparte') {
        const response = await apiService.buscarTemplateMultiParte(template.id);
        if (response.success && response.data && Array.isArray((response.data as any).partes)) {
          const partes = ((response.data as any).partes as any[])
            .map((parte: any, index: number) => ({
              nome: typeof parte?.nome === 'string' && parte.nome.trim() !== ''
                ? parte.nome
                : `Parte ${index + 1}`
            }));
          setPartesTemplate(partes);
          setPartesSelecionadas(partes.map((parte) => parte.nome));
        } else {
          setPartesTemplate([]);
          setPartesSelecionadas([]);
        }
        return;
      }

      const response = await apiService.buscarTemplate(template.id);
      if (response.success && response.data && Array.isArray((response.data as any).riscosPadrao)) {
        const riscos = (response.data as any).riscosPadrao as any[];
        const partesMap = new Map<string, boolean>();

        for (const risco of riscos) {
          const parteNome = typeof risco?.parte === 'string' && risco.parte.trim() !== ''
            ? risco.parte.trim()
            : 'Sem parte definida';
          partesMap.set(parteNome, true);
        }

        const partes = Array.from(partesMap.keys()).map((nome) => ({ nome }));
        setPartesTemplate(partes);
        setPartesSelecionadas(partes.map((parte) => parte.nome));
      } else {
        setPartesTemplate([]);
        setPartesSelecionadas([]);
      }
    } catch (error) {
      setPartesTemplate([]);
      setPartesSelecionadas([]);
    } finally {
      setCarregandoPartes(false);
    }
  };

  const handleSelecionarTemplate = (template: TemplateLista) => {
    setTemplateSelecionado(template);
    setErro(null);
    void carregarPartesTemplate(template);
  };

  const handleToggleParte = (nomeParte: string) => {
    setPartesSelecionadas((prev) => (
      prev.includes(nomeParte)
        ? prev.filter((parte) => parte !== nomeParte)
        : [...prev, nomeParte]
    ));
  };

  const handleAplicar = async () => {
    if (!templateSelecionado) {
      setErro(t('apreciacaoModais.selecionarTemplateMultiparte.erroSelecione'));
      return;
    }

    if (partesSelecionadas.length === 0) {
      setErro(t('apreciacaoModais.selecionarTemplateMultiparte.erroSelecioneParte'));
      return;
    }

    setErro(null);
    setAplicando(true);

    try {
      const response = templateSelecionado.origem === 'multiparte'
        ? await apiService.aplicarTemplateMultiParte(
          templateSelecionado.id,
          apreciacaoId,
          partesSelecionadas
        )
        : await apiService.aplicarTemplate(
          templateSelecionado.id,
          apreciacaoId,
          partesSelecionadas
        );

      if (response.success) {
        setTemplateSelecionado(null);
        if (onSucesso) {
          onSucesso();
        }
        onFechar();
      } else {
        setErro(response.message || t('apreciacaoModais.selecionarTemplateMultiparte.erroAplicar'));
      }
    } catch (error) {
      console.error('Erro ao aplicar template:', error);
      setErro(t('apreciacaoModais.selecionarTemplateMultiparte.erroAplicar'));
    } finally {
      setAplicando(false);
    }
  };

  const handleFechar = () => {
    if (!aplicando) {
      setBusca('');
      setFiltroGrupo('');
      setFiltroTipoMaquina('');
      setTemplateSelecionado(null);
      setPartesTemplate([]);
      setPartesSelecionadas([]);
      setErro(null);
      onFechar();
    }
  };

  return (
    <Dialog.Root open={aberto} onOpenChange={handleFechar}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl w-[90vw] max-w-4xl max-h-[85vh] overflow-hidden z-50 flex flex-col">
          <div className="p-6 flex-shrink-0">
            <div className="flex items-center justify-between mb-6">
              <Dialog.Title className="text-xl font-bold text-gray-900">
                {t('apreciacaoModais.selecionarTemplateMultiparte.titulo')}
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="text-gray-400 hover:text-gray-600"
                  disabled={aplicando}
                >
                  <X className="h-5 w-5" />
                </button>
              </Dialog.Close>
            </div>

            <div className="mb-4 space-y-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder={t('apreciacaoModais.selecionarTemplateMultiparte.buscaPlaceholder')}
                  className="w-full border border-gray-300 rounded-md px-10 py-2 text-sm"
                  disabled={aplicando || carregando}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('apreciacaoModais.selecionarTemplateMultiparte.filtroGrupoRotulo')}
                  </label>
                  <select
                    value={filtroGrupo}
                    onChange={(e) => setFiltroGrupo(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    disabled={aplicando || carregando}
                  >
                    <option value="">{t('apreciacaoModais.selecionarTemplateMultiparte.filtroGrupoTodos')}</option>
                    {grupos.map((grupo) => (
                      <option key={grupo.id} value={grupo.id}>
                        {grupo.nome}
                      </option>
                    ))}
                  </select>
                  {filtroGrupo && (
                    <p className="text-[11px] text-amber-700 mt-1">
                      {t('apreciacaoModais.selecionarTemplateMultiparte.avisoGrupoFiltrado')}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    {t('apreciacaoModais.selecionarTemplateMultiparte.filtroTipoRotulo')}
                  </label>
                  <select
                    value={filtroTipoMaquina}
                    onChange={(e) => setFiltroTipoMaquina(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    disabled={aplicando || carregando}
                  >
                    <option value="">{t('apreciacaoModais.selecionarTemplateMultiparte.filtroTipoTodos')}</option>
                    {tiposMaquinaUnicos.map((tipo) => (
                      <option key={tipo} value={tipo}>
                        {tipo}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {erro && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3 mb-4">
                <p className="text-sm text-red-800">{erro}</p>
              </div>
            )}
          </div>

          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {carregando ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-600">{t('apreciacaoModais.selecionarTemplateMultiparte.carregando')}</span>
              </div>
            ) : templatesCombinados.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm text-gray-500">
                  {busca || filtroGrupo || filtroTipoMaquina
                    ? t('apreciacaoModais.selecionarTemplateMultiparte.vazioFiltros')
                    : t('apreciacaoModais.selecionarTemplateMultiparte.vazioLista')}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {templatesCombinados.map((template) => {
                  const selecionado = Boolean(
                    templateSelecionado &&
                    templateSelecionado.id === template.id &&
                    templateSelecionado.origem === template.origem
                  );

                  return (
                    <div key={`${template.origem}:${template.id}`}>
                      <button
                        type="button"
                        onClick={() => handleSelecionarTemplate(template)}
                        disabled={aplicando}
                        className={`w-full text-left p-4 border-2 transition-colors ${selecionado
                          ? 'border-blue-500 bg-blue-50 rounded-t-md'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50 rounded-md'
                          } ${aplicando ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <h3 className="font-semibold text-gray-900">{template.nome}</h3>
                              <span className={`px-2 py-1 rounded text-[11px] font-medium ${template.origem === 'multiparte'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-emerald-100 text-emerald-800'
                                }`}>
                                {template.origem === 'multiparte' ? t('apreciacaoModais.selecionarTemplateMultiparte.badgeMultiparte') : t('apreciacaoModais.selecionarTemplateMultiparte.badgeSimples')}
                              </span>
                              {selecionado && <Check className="h-4 w-4 text-blue-600" />}
                            </div>

                            {template.descricao && (
                              <p className="text-sm text-gray-600 mb-2">{template.descricao}</p>
                            )}

                            <div className="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
                              {template.grupoNome && (
                                <span className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded">
                                  {template.grupoNome}
                                </span>
                              )}
                              {template.tipoMaquina && (
                                <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
                                  {template.tipoMaquina}
                                </span>
                              )}
                              {template.norma && (
                                <span className="bg-slate-100 text-slate-800 px-2 py-1 rounded">
                                  {template.norma}
                                </span>
                              )}
                              <span>{t('apreciacaoModais.selecionarTemplateMultiparte.partes', { total: template.totalPartes })}</span>
                              <span>{t('apreciacaoModais.selecionarTemplateMultiparte.riscos', { total: template.totalRiscos })}</span>
                              <span>{t('apreciacaoModais.selecionarTemplateMultiparte.criadoEm', { data: formatarData(template.createdAt) })}</span>
                            </div>
                          </div>
                        </div>
                      </button>

                      {selecionado && (
                        <div className="border-2 border-t-0 border-blue-500 rounded-b-md p-4 bg-gray-50">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="text-sm font-semibold text-gray-800">
                              {t('apreciacaoModais.selecionarTemplateMultiparte.partesTitulo')}
                            </h4>
                            <span className="text-xs text-gray-500">
                              {t('apreciacaoModais.selecionarTemplateMultiparte.partesContador', { selecionadas: partesSelecionadas.length, total: partesTemplate.length })}
                            </span>
                          </div>

                          {carregandoPartes ? (
                            <div className="flex items-center text-sm text-gray-600 py-2">
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              {t('apreciacaoModais.selecionarTemplateMultiparte.carregandoPartes')}
                            </div>
                          ) : partesTemplate.length === 0 ? (
                            <p className="text-sm text-gray-500">{t('apreciacaoModais.selecionarTemplateMultiparte.semPartes')}</p>
                          ) : (
                            <div className="space-y-2 max-h-40 overflow-y-auto">
                              {partesTemplate.map((parte) => (
                                <label key={parte.nome} className="flex items-center gap-2 text-sm text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={partesSelecionadas.includes(parte.nome)}
                                    onChange={() => handleToggleParte(parte.nome)}
                                    disabled={aplicando}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  {parte.nome}
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="p-6 border-t border-gray-200 flex-shrink-0">
            <div className="flex justify-end space-x-3">
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  disabled={aplicando}
                >
                  {t('comum.acao.cancelar')}
                </button>
              </Dialog.Close>
              <button
                type="button"
                onClick={handleAplicar}
                disabled={aplicando || !templateSelecionado}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {aplicando ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('apreciacaoModais.selecionarTemplateMultiparte.aplicando')}
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {t('apreciacaoModais.selecionarTemplateMultiparte.botaoAplicar')}
                  </>
                )}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
